import React from 'react';
import { motion } from 'framer-motion';

const FFSlide08DualStrategy: React.FC = () => {
  return (
    <div className="flex flex-col h-full relative overflow-hidden">
      {/* Atmospheric background */}
      <div className="absolute inset-0">
        <div
          className="absolute inset-0"
          style={{
            background: 'radial-gradient(ellipse 100% 70% at 20% 100%, rgba(16, 185, 129, 0.1) 0%, transparent 50%), radial-gradient(ellipse 100% 70% at 80% 100%, rgba(14, 165, 233, 0.1) 0%, transparent 50%)',
          }}
        />
        <div
          className="absolute inset-0 opacity-[0.015]"
          style={{
            backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255,255,255,0.1) 2px, rgba(255,255,255,0.1) 4px)',
          }}
        />
        <div
          className="absolute inset-0"
          style={{
            background: 'radial-gradient(ellipse at center, transparent 0%, rgba(0,0,0,0.3) 100%)',
          }}
        />
      </div>

      {/* Content */}
      <div className="relative z-10 flex flex-col h-full px-16 py-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h2 className="text-5xl font-bold text-white tracking-tight mb-2">
            Go-To-Market Strategy
          </h2>
          <p className="text-xl text-[#94a3b8]">
            Bifurcated approach: high-touch enterprise sales + high-velocity digital acquisition
          </p>
        </motion.div>

        {/* Two columns */}
        <div className="flex-1 grid grid-cols-2 gap-12">
          {/* B2B Column */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="relative"
          >
            {/* Glow accent */}
            <div className="absolute -left-3 top-0 bottom-0 w-1 bg-gradient-to-b from-[#10b981] via-[#10b981]/50 to-transparent rounded-full" />
            <div className="absolute -left-3 top-0 w-1 h-20 bg-[#10b981] rounded-full blur-md" />

            <div className="pl-6">
              {/* Header */}
              <div className="mb-6">
                <div className="text-sm text-[#10b981] tracking-widest uppercase mb-2">Enterprise</div>
                <h3 className="text-3xl font-bold text-white">B2B Strategy</h3>
                <p className="text-[#94a3b8] mt-1">Independent School Penetration</p>
              </div>

              {/* Tri-State Model */}
              <div className="mb-6">
                <div className="text-xs text-[#64748b] uppercase tracking-wider mb-4">Tri-State Deployment Model</div>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { state: 'NSW', city: 'Sydney', focus: 'GPS, CAS schools' },
                    { state: 'VIC', city: 'Melbourne', focus: 'APS/AGSV schools' },
                    { state: 'QLD', city: 'Brisbane', focus: 'Growth corridor' },
                  ].map((region) => (
                    <div
                      key={region.state}
                      className="p-4 rounded border border-[#10b981]/20 text-center"
                      style={{ background: 'rgba(16, 185, 129, 0.05)' }}
                    >
                      <div className="text-2xl font-bold text-[#10b981]">{region.state}</div>
                      <div className="text-sm text-white">{region.city}</div>
                      <div className="text-xs text-[#64748b] mt-1">{region.focus}</div>
                    </div>
                  ))}
                </div>
                <p className="text-sm text-[#64748b] mt-3">
                  1 Account Executive per state — local credibility drives trust
                </p>
              </div>

              {/* Sales Motion */}
              <div className="mb-6">
                <div className="text-xs text-[#64748b] uppercase tracking-wider mb-3">Sales Motion</div>
                <div className="space-y-2">
                  {[
                    'Account-Based Marketing targeting top 50 schools per state',
                    'Presence at AISNSW, ISV, ISQ conferences',
                    'Paid pedagogical pilots → school-wide rollout',
                    '"Reference Site" effect creates peer FOMO',
                  ].map((item) => (
                    <div key={item} className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 bg-[#10b981] rounded-full mt-2 flex-shrink-0" />
                      <span className="text-[#e2e8f0]">{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Key insight */}
              <div
                className="p-4 rounded border-l-2 border-[#10b981]"
                style={{ background: 'rgba(16, 185, 129, 0.08)' }}
              >
                <p className="text-[#94a3b8] text-sm">
                  <span className="text-white font-medium">Why this works:</span> Independent schools are highly networked.
                  Winning a flagship like PLC (Knox in the works) creates immediate demand from peers.
                </p>
              </div>
            </div>
          </motion.div>

          {/* B2C Column */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="relative"
          >
            {/* Glow accent */}
            <div className="absolute -left-3 top-0 bottom-0 w-1 bg-gradient-to-b from-[#0ea5e9] via-[#0ea5e9]/50 to-transparent rounded-full" />
            <div className="absolute -left-3 top-0 w-1 h-20 bg-[#0ea5e9] rounded-full blur-md" />

            <div className="pl-6">
              {/* Header */}
              <div className="mb-6">
                <div className="text-sm text-[#0ea5e9] tracking-widest uppercase mb-2">Consumer</div>
                <h3 className="text-3xl font-bold text-white">B2C Strategy</h3>
                <p className="text-[#94a3b8] mt-1">Parent-Led Digital Acquisition</p>
              </div>

              {/* Core philosophy */}
              <div
                className="p-4 rounded border-l-2 border-[#0ea5e9] mb-6"
                style={{ background: 'rgba(14, 165, 233, 0.08)' }}
              >
                <p className="text-white italic">
                  "Parents need to feel <span className="text-[#0ea5e9] font-semibold">relief</span> within the first session"
                </p>
              </div>

              {/* Acquisition Channels */}
              <div className="mb-6">
                <div className="text-xs text-[#64748b] uppercase tracking-wider mb-3">Acquisition Channels</div>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { channel: 'Paid Social', detail: 'Meta + TikTok', target: 'Parents 35–55' },
                    { channel: 'SEO & Content', detail: 'Authority play', target: 'Curriculum guides' },
                    { channel: 'Influencers', detail: 'Trust validation', target: 'Parenting bloggers' },
                  ].map((item) => (
                    <div
                      key={item.channel}
                      className="p-3 rounded border border-white/10 text-center"
                      style={{ background: 'rgba(255,255,255,0.02)' }}
                    >
                      <div className="text-sm font-medium text-white">{item.channel}</div>
                      <div className="text-xs text-[#0ea5e9]">{item.detail}</div>
                      <div className="text-xs text-[#64748b] mt-1">{item.target}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Conversion Funnel */}
              <div className="mb-6">
                <div className="text-xs text-[#64748b] uppercase tracking-wider mb-3">Conversion Funnel</div>
                <div className="flex items-center gap-2">
                  {[
                    { step: 'Ad → Trial', desc: 'Parent signs up' },
                    { step: 'Diagnostic', desc: '"Aha" moment' },
                    { step: 'Progress Emails', desc: 'Weekly retention' },
                  ].map((item, i) => (
                    <React.Fragment key={item.step}>
                      <div className="flex-1 p-3 rounded border border-[#0ea5e9]/20 text-center" style={{ background: 'rgba(14, 165, 233, 0.05)' }}>
                        <div className="text-sm font-medium text-[#0ea5e9]">{item.step}</div>
                        <div className="text-xs text-[#94a3b8]">{item.desc}</div>
                      </div>
                      {i < 2 && <span className="text-[#334155]">→</span>}
                    </React.Fragment>
                  ))}
                </div>
              </div>

              {/* Key insight */}
              <div
                className="p-4 rounded border-l-2 border-[#0ea5e9]"
                style={{ background: 'rgba(14, 165, 233, 0.08)' }}
              >
                <p className="text-[#94a3b8] text-sm">
                  <span className="text-white font-medium">The hook:</span> Rapid diagnostic shows parents exactly where
                  their child's gaps are — immediate value before subscription.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default FFSlide08DualStrategy;
